import type {
  CanvasEditorSnapshot,
  CanvasTransientGeometry,
} from "../controller/types";
import { nodeBounds } from "../domain/canvas";
import type { CanvasNode, EntityId, Point } from "../domain/types";

export type CanvasExtensibleInteractionKind =
  | "rotate"
  | "lasso"
  | (string & {});

export interface CanvasInteractionStrategyContext {
  snapshot: CanvasEditorSnapshot;
  start: Point;
  nodeIds: readonly EntityId[];
  additive?: boolean;
}

export interface CanvasStrategyResult {
  geometry?: readonly CanvasTransientGeometry[];
  selection?: {
    nodeIds?: readonly EntityId[];
    edgeIds?: readonly EntityId[];
    primaryId?: EntityId;
  };
}

export interface CanvasInteractionStrategy<TState = unknown> {
  readonly id: string;
  readonly kind: CanvasExtensibleInteractionKind;
  begin(context: CanvasInteractionStrategyContext): TState | null;
  update(
    state: TState,
    point: Point,
    snapshot: CanvasEditorSnapshot,
  ): CanvasStrategyResult & { state: TState };
  complete?(
    state: TState,
    snapshot: CanvasEditorSnapshot,
  ): CanvasStrategyResult | void;
  cancel?(state: TState): void;
}

/**
 * Registry boundary for optional interactions such as rotation and lasso.
 * Built-in pointer gestures stay small, while product packages can register
 * durable strategies without adding persistence or renderer dependencies.
 */
export class CanvasInteractionStrategyRegistry {
  readonly #strategies = new Map<string, CanvasInteractionStrategy>();

  constructor(strategies: Iterable<CanvasInteractionStrategy> = []) {
    for (const strategy of strategies) this.register(strategy);
  }

  register(strategy: CanvasInteractionStrategy): () => void {
    if (this.#strategies.has(strategy.id)) {
      throw new Error(
        `Canvas interaction strategy already exists: ${strategy.id}`,
      );
    }
    this.#strategies.set(strategy.id, strategy);
    return () => {
      if (this.#strategies.get(strategy.id) === strategy) {
        this.#strategies.delete(strategy.id);
      }
    };
  }

  get(id: string): CanvasInteractionStrategy | undefined {
    return this.#strategies.get(id);
  }

  list(
    kind?: CanvasExtensibleInteractionKind,
  ): readonly CanvasInteractionStrategy[] {
    const strategies = [...this.#strategies.values()];
    return kind === undefined
      ? strategies
      : strategies.filter((strategy) => strategy.kind === kind);
  }
}

interface RotationState {
  startAngle: number;
  center: Point;
  nodes: Array<{
    id: EntityId;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }>;
}

interface LassoState {
  points: Point[];
  additive: boolean;
}

function pointInPolygon(point: Point, polygon: readonly Point[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (
    let current = 0, previous = polygon.length - 1;
    current < polygon.length;
    previous = current++
  ) {
    const a = polygon[current]!;
    const b = polygon[previous]!;
    if (
      a.y > point.y !== b.y > point.y &&
      point.x <
        ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || Number.EPSILON) +
          a.x
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function selectedNodes(
  snapshot: CanvasEditorSnapshot,
  ids: readonly EntityId[],
): CanvasNode[] {
  const selected = new Set(ids);
  return snapshot.canvas.nodes.filter((node) => selected.has(node.id));
}

/** Built-in rotation strategy used by the React board and available to hosts. */
export const canvasRotationStrategy: CanvasInteractionStrategy<RotationState> =
  {
    id: "canvas.rotate",
    kind: "rotate",
    begin({ snapshot, start, nodeIds }) {
      const nodes = selectedNodes(snapshot, nodeIds);
      if (nodes.length === 0) return null;
      const minX = Math.min(...nodes.map((node) => nodeBounds(node).minX));
      const minY = Math.min(...nodes.map((node) => nodeBounds(node).minY));
      const maxX = Math.max(...nodes.map((node) => nodeBounds(node).maxX));
      const maxY = Math.max(...nodes.map((node) => nodeBounds(node).maxY));
      const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
      return {
        center,
        startAngle: Math.atan2(start.y - center.y, start.x - center.x),
        nodes: nodes.map((node) => ({
          id: node.id,
          x: node.geometry.x,
          y: node.geometry.y,
          width: node.geometry.width,
          height: node.geometry.height,
          rotation: node.geometry.rotation,
        })),
      };
    },
    update(state, point) {
      const delta =
        ((Math.atan2(point.y - state.center.y, point.x - state.center.x) -
          state.startAngle) *
          180) /
        Math.PI;
      return {
        state,
        geometry: state.nodes.map((node) => {
          const radians = (delta * Math.PI) / 180;
          const centerX = node.x + node.width / 2;
          const centerY = node.y + node.height / 2;
          const relativeX = centerX - state.center.x;
          const relativeY = centerY - state.center.y;
          const rotatedCenterX =
            state.center.x +
            relativeX * Math.cos(radians) -
            relativeY * Math.sin(radians);
          const rotatedCenterY =
            state.center.y +
            relativeX * Math.sin(radians) +
            relativeY * Math.cos(radians);
          return {
            id: node.id,
            x: rotatedCenterX - node.width / 2,
            y: rotatedCenterY - node.height / 2,
            rotation: node.rotation + delta,
          };
        }),
      };
    },
  };

/** Built-in freehand lasso strategy. Nodes are selected by their visual center. */
export const canvasLassoStrategy: CanvasInteractionStrategy<LassoState> = {
  id: "canvas.lasso",
  kind: "lasso",
  begin({ start, additive }) {
    return { points: [start], additive: additive === true };
  },
  update(state, point) {
    return {
      state: {
        ...state,
        points: [...state.points, point],
      },
    };
  },
  complete(state, snapshot) {
    const enclosed = snapshot.canvas.nodes
      .filter((node) =>
        pointInPolygon(
          {
            x: node.geometry.x + node.geometry.width / 2,
            y: node.geometry.y + node.geometry.height / 2,
          },
          state.points,
        ),
      )
      .map((node) => node.id);
    const nodeIds = state.additive
      ? [...new Set([...snapshot.selection.nodeIds, ...enclosed])]
      : enclosed;
    const primaryId = enclosed.at(-1);
    return {
      selection: {
        nodeIds,
        ...(primaryId === undefined ? {} : { primaryId }),
      },
    };
  },
};

export function createDefaultCanvasInteractionStrategyRegistry(): CanvasInteractionStrategyRegistry {
  return new CanvasInteractionStrategyRegistry([
    canvasRotationStrategy,
    canvasLassoStrategy,
  ]);
}
